import React from 'react';
import PropTypes from 'prop-types';
import ThumbnailUtil from '../../util/ThumbnailUtil';
import CommentUtil from '../../util/CommentUtil';
import Authenticator from '../../protocol/Authenticator';
import Action from '../../common/Action';
import _ from 'lodash';
import TextareaSuggestions, { textareaSuggestionsProps } from '../../hoc/TextareaSuggestions';
import StringUtil from '../../util/StringUtil';
import AtMentionUserNotification from '../../components/comments/AtMentionUserNotification';
import StoreListener from '../../store/StoreListener';
import { COLLECTION_USERS, COMMENT_TEXT, PERMISSIONS } from '../../store/Constants';
import Store from '../../store/Store';
import { isMobile } from '../../util/ScreenUtil';
import ShowMoreText from './ShowMoreText';
import t from '../../i18n/I18n';
import { ThemeSetup } from '../../themes/ThemeSetup';

const debug = require('../../debug/debug')('wc:comments:CommentReply');

const getTextarea = (component) => component.refs.textarea;
const getSuggestions = (component) => component.getSuggestions();
const getAcceptSuggestion = (component) => component.acceptSuggestion;
const storeItems = [PERMISSIONS];

@StoreListener(storeItems)
@TextareaSuggestions(getTextarea, getSuggestions, getAcceptSuggestion)
export default class CommentReply extends React.Component {
  static propTypes = {
    reply: PropTypes.object.isRequired,
    thread: PropTypes.object.isRequired,
    onEdit: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    onEditStart: PropTypes.func.isRequired,
    getCommentString: PropTypes.func.isRequired,
    [PERMISSIONS]: PropTypes.object,
    ...textareaSuggestionsProps
  };

  constructor(props) {
    super(props);
    this.state = {
      editText: '',
      showButtons: false,
      editMode: false,
      creatorFullName: this.getCreatorFromProps(),
      atMentions: [],
      isCreator: false
    };

    this.suggestions = this.getSuggestions();
  }

  componentWillMount() {
    this.loadCreatorFullname();
  }

  componentDidMount() {
    this.isCreator();
  }

  shouldComponentUpdate(nextProps, nextState) {
    const { editText, editMode } = nextState;
    if (!editMode) {
      return true;
    }

    return editText !== this.state.editText;
  }

  componentWillUnmount() {
    Store.put(COMMENT_TEXT, '');
  }

  getCreatorFromProps() {
    return this.props.reply.getCreator();
  }

  loadCreatorFullname() {
    if (!this.getCreatorFromProps()) return;
    ThumbnailUtil.loadUserFullName(this.getCreatorFromProps())
      .then((fullName) => {
        this.setState({ creatorFullName: fullName });
      })
      .catch((errorId) => {
        this.setState({ creatorFullName: 'Anonymous' });
        console.error(errorId);
      });
  }

  getSuggestions = () => {
    return Store.get(COLLECTION_USERS, []).map((u) => ({
      value: u._userId,
      label: u._displayName || u._email,
      icon: ThumbnailUtil.avatar(u._userId)
    }));
  };

  acceptSuggestion = (suggestion, text) => {
    const atMentions = this.getMentionedUsers(text);
    this.setState({ editText: text, atMentions });
  };

  onTextChange = (e) => {
    const { value } = e.target;
    const atMentions = this.getMentionedUsers(value);
    this.setState({ editText: value, atMentions });
    this.props.onTextChange(e);
  };

  getMentionedUsers = (comment) => {
    const usersArray = StringUtil.parseTextForAtMentions(comment, this.suggestions);
    const atMentions = this.suggestions.filter((user) => usersArray.indexOf(user.label) > -1);
    return atMentions;
  };

  onSaveClick = async (e: MouseEvent) => {
    try {
      const { reply } = this.props;
      const { editMode, editText } = this.state;
      e.preventDefault();
      e.stopPropagation();
      if (editMode) {
        if (_.size(editText) > 0 && editText.trim().length > 0 && editText.trim() !== reply.getText().trim()) {
          const updatedReply = _.cloneDeep(reply);
          updatedReply.setText(editText);
          await this.props.onEdit(updatedReply);
          this.props.onEditStart();
        }
        setTimeout(() => {
          this.setState({ editMode: false, editText: '' });
        }, 200);
      } else {
        this.setState({ editMode: true, editText: reply.getText() });
        this.props.onEditStart();
      }
    } catch (err) {
      debug('[onSaveClick()] error: ', err);
    }
  };

  isCreator = () => {
    const isCreator = CommentUtil.isReplyCreator(this.props.reply);
    if (isCreator) {
      this.setState({ isCreator: true });
    }
  };

  render() {
    const { reply, thread, getCommentString } = this.props;
    const { editMode, editText, creatorFullName, atMentions, isCreator } = this.state;
    const { isAdmin, canWriteComments } = this.props[PERMISSIONS];
    const allowActions = (canWriteComments && isCreator) || isAdmin;
    const styles = getStyle();
    const isUnread = CommentUtil.isUnread(thread, reply);
    const opacity = isUnread ? 1 : 0;
    const unreadMarker = (
      <div className="unreadMarker" style={{ opacity }}>
        {t('NEW')}
      </div>
    );
    return (
      <div
        onMouseEnter={() => this.setState({ showButtons: true })}
        onMouseLeave={() => this.setState({ showButtons: false })}
        style={{ position: 'relative', cursor: 'pointer' }}
        onClick={this.onClick}>
        {unreadMarker}
        <div style={styles.entry}>
          <div style={{ float: 'left', width: '60px', height: '85px' }}>
            <div
              style={{ ...styles.avatar, backgroundImage: `url(${ThumbnailUtil.avatar(this.getCreatorFromProps())})` }}
            />
            {(this.state.showButtons || isMobile()) && (
              <div style={styles.editButtonsBlock}>
                <Action custom predicates={[() => allowActions]}>
                  {this.state.editMode ? (
                    <div
                      style={{
                        ...styles.applyBtn,
                        visibility: this.state.showButtons || isMobile() ? 'visible' : 'hidden'
                      }}
                      onMouseDown={(e) => {
                        this.onSaveClick(e);
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        ...styles.editBtn,
                        visibility: this.state.showButtons || isMobile() ? 'visible' : 'hidden'
                      }}
                      onMouseDown={(e) => {
                        this.onSaveClick(e);
                      }}
                    />
                  )}
                </Action>
                <Action custom predicates={[() => allowActions]}>
                  <div
                    style={{
                      ...styles.removeBtn,
                      visibility: this.state.showButtons || isMobile() ? 'visible' : 'hidden'
                    }}
                    onClick={(event) => {
                      event.stopPropagation();
                      this.props.onRemove(reply);
                    }}
                  />
                </Action>
              </div>
            )}
          </div>
          <div style={{ float: 'left', width: 'calc(100% - 70px)' }}>
            <div style={styles.author} title={creatorFullName}>
              {creatorFullName}
            </div>
            <div style={styles.lastEdit}>{CommentUtil.drawEditedOrModified(reply)}</div>
            {!editMode ? (
              <div style={styles.text}>
                <ShowMoreText lines={4}>
                  <p className="textparagraph">{getCommentString(reply.getText())}</p>
                </ShowMoreText>
              </div>
            ) : (
              <div>
                <div>
                  <textarea
                    id="replybox"
                    value={editText}
                    onChange={this.onTextChange}
                    ref="textarea"
                    placeholder="Reply..."
                    style={styles.textarea}
                    onBlur={(e) => {
                        this.onSaveClick(e);
                    }}
                    autoFocus={true}
                  />
                </div>
                <AtMentionUserNotification atMentions={atMentions} />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  onClick = () => {
    const { reply, thread } = this.props;
    const isUnread = CommentUtil.isUnread(thread, reply);
    if (isUnread) {
      CommentUtil.markCommentAsReadIfNeeded(thread, true);
    }
  };
}

function getStyle() {
  return {
    repliesSeparatorText: {
      display: 'inline-block',
      color: '#B5B5B5',
      fontSize: '12px',
      fontWeight: 600,
      letterSpacing: '1px',
      lineHeight: '15px',
      verticalAlign: 'middle',
      marginRight: '10px'
    },
    repliesSeparator: {
      display: 'inline-block',
      color: 'var(--primary-font-color',
      verticalAlign: 'middle',
      borderTop: '1px solid var(--primary-box-shadow)',
      width: 'calc(100% - 70px)'
    },
    entry: {
      overflow: 'hidden',
      paddingLeft: '30px'
    },
    editButtonsBlock: {
      marginTop: '5px'
    },
    lastEdit: {
      color: 'var(--primary-font-color-selected)',
      fontSize: '11px',
      lineHeight: '13px'
    },
    avatar: {
      marginTop: '17px',
      borderRadius: '50%',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover',
      height: '45px',
      width: '45px'
    },
    author: {
      marginTop: '10px',
      color: 'var(--primary-font-color-selected)',
      fontSize: '14px',
      lineHeight: '20px',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap'
    },
    text: {
      marginTop: '5px',
      fontSize: '14px',
      lineHeight: '18px',
      color: 'var(--primary-font-color-selected)',
      wordWrap: 'break-word',
      whiteSpace: 'pre-wrap'
    },
    editBtn: {
      display: 'inline-block',
      float: 'left',
      width: '16px',
      height: '16px',
      backgroundImage: ThemeSetup.getBackgroundURL('edit_asset.svg'),
      marginRight: '10px',
      cursor: 'pointer'
    },
    applyBtn: {
      display: 'inline-block',
      float: 'left',
      width: '16px',
      height: '16px',
      backgroundImage: ThemeSetup.getBackgroundURL('checkmark_red.svg'),
      backgroundSize: '100%',
      marginRight: '10px',
      cursor: 'pointer'
    },
    removeBtn: {
      display: 'inline-block',
      float: 'left',
      width: '16px',
      height: '16px',
      backgroundImage: ThemeSetup.getBackgroundURL('trash_red.svg'),
      cursor: 'pointer'
    },
    textarea: {
      width: '100%',
      height: '103px',
      border: '1px solid var(--primary-box-shadow)',
      backgroundColor:'var(--primary-header-bg)',
      color: 'var(--input-box-font-color)',
      marginTop: '8px',
      outline: 'none',
      resize: 'none',
      fontSize: '14px'
    }
  };
}
